/**
 * PostAndEmailDialog Component
 * 
 * Confirmation dialog for Post & Email feature.
 * Shows warning about using sparingly, with options to Post Only or Send Email.
 */

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useDesign } from '@/contexts/DesignContext';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { 
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { toast } from 'sonner';
import { 
  AlertTriangle,
  Send,
  Mail,
  Loader2
} from 'lucide-react';

export function PostAndEmailDialog({ 
  isOpen, 
  onClose, 
  onPostOnly,
  onPostAndEmail,
  isLoading = false,
  selectedGroups = []
}) {
  const { isStudio } = useDesign();
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const content = (
    <div className="space-y-4 py-2">
      {/* Warning Icon */}
      <div className="flex justify-center">
        <div className={isStudio ? 'st-pneglyph' : "w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center"}>
          <AlertTriangle className={isStudio ? undefined : "w-8 h-8 text-amber-600 dark:text-amber-400"} />
        </div>
      </div>

      {/* Warning Message */}
      <div className="text-center space-y-2">
        <p className="text-slate-700 dark:text-slate-200 font-medium">
          Are you sure?
        </p>
        <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
          This feature is only for very important info. Using it too frequently will train your team that they don't need to check the Team Feed.
        </p>
      </div>

      {/* Email Info */}
      <div className={isStudio ? 'st-pnenote' : "bg-slate-50 dark:bg-[#1e1f22] rounded-xl p-3 text-center"}>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          <Mail className="w-3.5 h-3.5 inline mr-1" />
          An email will be sent to <strong>{selectedGroups.length > 0 ? 'members of selected groups' : 'all team members'}</strong>
        </p>
      </div>

      {/* Buttons */}
      <div className={isStudio ? 'st-pnefoot' : "flex flex-col gap-2 pt-2"}>
        <Button
          variant="outline"
          onClick={onPostOnly}
          disabled={isLoading}
          className={isStudio ? 'st-btn st-sec' : "w-full h-12 rounded-xl"}
          data-testid="post-only-btn"
        >
          <Send className="w-4 h-4 mr-2" />
          Post Only
        </Button>
        
        <Button
          onClick={onPostAndEmail}
          disabled={isLoading}
          className={isStudio ? 'st-btn st-pri' : "w-full h-12 rounded-xl bg-indigo-600 hover:bg-indigo-700"}
          data-testid="post-and-email-btn"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Mail className="w-4 h-4 mr-2" />
              Yes, Send Email
            </>
          )}
        </Button>
        
        <Button
          variant="ghost"
          onClick={onClose}
          disabled={isLoading}
          className={isStudio ? 'st-btn' : "w-full h-10 text-slate-500 dark:text-slate-400"}
        >
          Cancel
        </Button>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent side="bottom" className="rounded-t-xl">
          <SheetHeader className="pb-2">
            <SheetTitle className="text-center">Post & Email Team</SheetTitle>
            <SheetDescription className="text-center">
              This will post to the feed and email everyone
            </SheetDescription>
          </SheetHeader>
          {content}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={isStudio ? 'st-pnedlg sm:max-w-md' : "sm:max-w-md"}>
        <DialogHeader className="pb-2">
          <DialogTitle className="text-center">Post & Email Team</DialogTitle>
          <DialogDescription className="text-center">
            This will post to the feed and email everyone
          </DialogDescription>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
}

export default PostAndEmailDialog;
